import { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import Auth from './Auth';
import { t } from './i18n';

// Ecranul de permisiune al prizei (6 octombrie 2026), ruta `/oauth/consent`.
//
// Cand un asistent AI (Claude) cere acces la contul cuiva prin priza, serverul de
// autentificare Supabase (OAuth 2.1) aduce omul aici, cu `?authorization_id=<id>`. Aici vede
// CINE cere, UNDE se intoarce si bifeaza clientii pentru care asistentul poate lucra. Nimic nu
// e bifat din oficiu: alegerea trebuie facuta, nu doar confirmata.
//
// Lista bifata se scrie in `priza_conexiuni` INAINTE de aprobare. Daca scrierea pica, nu se
// aproba nimic: o cheie de priza fara lista ei n-ar avea acces la niciun client, dar omul ar
// crede ca a ales. Vezi supabase/priza_conexiuni.sql si supabase/priza_lacat.sql.
//
// Deocamdata doar super_admin poate lega un asistent. Celelalte roluri vad un refuz explicit;
// aceeasi regula o aplica si functia prizei, deci ecranul nu e singura paza.

function idCerere() {
  return new URLSearchParams(window.location.search).get('authorization_id');
}

function gazda(adresa) {
  try { return new URL(adresa).host; } catch (e) { return adresa || ''; }
}

const cadru = { minHeight: '100vh', background: '#F2F8F4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', sans-serif", padding: 24 };
const carton = { background: 'white', borderRadius: 16, padding: '40px 48px', maxWidth: 460, width: '100%', border: '1.5px solid #D1E8DA', boxShadow: '0 4px 24px rgba(26,107,60,0.08)', boxSizing: 'border-box' };
const butonPlin = activ => ({ flex: 1, padding: '11px', background: activ ? '#1A6B3C' : '#D1E8DA', color: activ ? 'white' : '#6B7280', border: 'none', borderRadius: 10, fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500, cursor: activ ? 'pointer' : 'default' });
const butonGol = { flex: 1, padding: '11px', background: 'white', color: '#0F3D22', border: '1.5px solid #D1E8DA', borderRadius: 10, fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 500, cursor: 'pointer' };

function Antet() {
  return (
    <div style={{ textAlign: 'center', marginBottom: 24 }}>
      <div style={{ width: 44, height: 44, borderRadius: 10, background: '#1A6B3C', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontFamily: "'DM Serif Display', serif", fontSize: 20, color: 'white' }}>N</div>
      <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, color: '#0F3D22', letterSpacing: -0.5 }}>NM Social</div>
    </div>
  );
}

function Mesaj({ text, children }) {
  return (
    <div style={cadru}>
      <div style={{ ...carton, textAlign: 'center' }}>
        <Antet />
        <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, margin: 0 }}>{text}</p>
        {children}
      </div>
    </div>
  );
}

export default function PermitePriza() {
  const id = idCerere();
  // `undefined` = inca nu stim daca e cineva logat; `null` = nimeni.
  const [sesiune, setSesiune] = useState(undefined);
  const [stare, setStare] = useState('incarca'); // incarca | alege | doarAdmin | trimite | invalida
  const [detalii, setDetalii] = useState(null);
  const [clienti, setClienti] = useState([]);
  const [alese, setAlese] = useState([]);
  const [eroare, setEroare] = useState('');

  useEffect(() => {
    let activ = true;
    supabase.auth.getSession().then(({ data }) => { if (activ) setSesiune(data.session || null); });
    const { data: ascultare } = supabase.auth.onAuthStateChange((_ev, s) => { if (activ) setSesiune(s || null); });
    return () => { activ = false; ascultare.subscription.unsubscribe(); };
  }, []);

  const userId = sesiune && sesiune.user ? sesiune.user.id : null;

  useEffect(() => {
    if (!id || !userId) return undefined;
    let activ = true;
    (async () => {
      setStare('incarca');
      const { data: profil } = await supabase.from('profiluri').select('rol').eq('user_id', userId).maybeSingle();
      if (!activ) return;
      if (!profil || profil.rol !== 'super_admin') { setStare('doarAdmin'); return; }

      const { data, error } = await supabase.auth.oauth.getAuthorizationDetails(id);
      if (!activ) return;
      if (error || !data) { setStare('invalida'); return; }
      // Omul a permis deja aceasta aplicatie: serverul nu mai cere acordul, doar ne trimite inapoi.
      if (!data.authorization_id && data.redirect_url) { window.location.assign(data.redirect_url); return; }

      const { data: lista, error: errClienti } = await supabase.from('clienti').select('id, nume').order('nume');
      if (!activ) return;
      if (errClienti) { setEroare(t('priza', 'eroareIncarcare', { detaliu: errClienti.message })); }
      setDetalii(data);
      setClienti(lista || []);
      setStare('alege');
    })();
    return () => { activ = false; };
  }, [id, userId]);

  function comuta(clientId) {
    setEroare('');
    setAlese(a => (a.includes(clientId) ? a.filter(x => x !== clientId) : [...a, clientId]));
  }

  async function permite() {
    if (!alese.length) { setEroare(t('priza', 'alegeUnul')); return; }
    setStare('trimite');
    setEroare('');
    const { error: errLista } = await supabase.from('priza_conexiuni').upsert({
      user_id: userId,
      oauth_client_id: detalii.client.id,
      clienti: alese,
      actualizat_la: new Date().toISOString(),
    });
    if (errLista) {
      setEroare(t('priza', 'eroareSalvare', { detaliu: errLista.message }));
      setStare('alege');
      return;
    }
    const { data, error } = await supabase.auth.oauth.approveAuthorization(id, { skipBrowserRedirect: true });
    if (error || !data || !data.redirect_url) {
      setEroare(t('priza', 'eroareAprobare', { detaliu: (error && error.message) || '' }));
      setStare('alege');
      return;
    }
    window.location.assign(data.redirect_url);
  }

  async function refuza() {
    setStare('trimite');
    setEroare('');
    const { data, error } = await supabase.auth.oauth.denyAuthorization(id, { skipBrowserRedirect: true });
    if (error || !data || !data.redirect_url) { setStare('invalida'); return; }
    window.location.assign(data.redirect_url);
  }

  if (!id) return <Mesaj text={t('priza', 'faraCerere')} />;
  if (sesiune === undefined) return <Mesaj text={t('priza', 'seIncarca')} />;
  // Nimeni logat: ecranul obisnuit de intrare. Dupa intrare, sesiunea se schimba si ajungem
  // mai jos, cu aceeasi adresa, deci cu aceeasi cerere.
  if (!sesiune) return <Auth />;
  if (stare === 'incarca') return <Mesaj text={t('priza', 'seIncarca')} />;
  if (stare === 'invalida') return <Mesaj text={t('priza', 'cerereInvalida')} />;
  if (stare === 'doarAdmin') {
    return (
      <Mesaj text={t('priza', 'doarAdmin')}>
        <div style={{ display: 'flex', marginTop: 24 }}>
          <button type="button" onClick={refuza} style={butonGol}>{t('priza', 'refuz')}</button>
        </div>
      </Mesaj>
    );
  }

  const seTrimite = stare === 'trimite';
  const poatePermite = !seTrimite && alese.length > 0;

  return (
    <div style={cadru}>
      <div style={carton}>
        <Antet />
        <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, color: '#0F3D22', margin: '0 0 12px', textAlign: 'center', fontWeight: 400 }}>{t('priza', 'titlu')}</h1>
        <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, margin: '0 0 6px' }}>
          {t('priza', 'cere', { aplicatie: detalii.client.name, email: detalii.user.email })}
        </p>
        <p style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.6, margin: '0 0 20px' }}>
          {t('priza', 'intoarcere', { gazda: gazda(detalii.redirect_uri) })}
        </p>

        <div style={{ fontSize: 12, fontWeight: 600, color: '#4B5563', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 6 }}>{t('priza', 'alegeClienti')}</div>
        <p style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.5, margin: '0 0 10px' }}>{t('priza', 'alegeClientiTxt')}</p>
        <div style={{ border: '1.5px solid #D1E8DA', borderRadius: 10, maxHeight: 260, overflowY: 'auto', marginBottom: 16 }}>
          {clienti.length === 0 && <div style={{ padding: '12px 14px', fontSize: 13, color: '#4B5563' }}>{t('priza', 'niciunClient')}</div>}
          {clienti.map((c, i) => (
            <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', fontSize: 14, color: '#111827', cursor: seTrimite ? 'default' : 'pointer', borderTop: i ? '1px solid #E4EFE8' : 'none' }}>
              <input type="checkbox" checked={alese.includes(c.id)} disabled={seTrimite} onChange={() => comuta(c.id)} style={{ width: 16, height: 16, accentColor: '#1A6B3C' }} />
              {c.nume}
            </label>
          ))}
        </div>

        {eroare && <div style={{ background: '#FEE2E2', color: '#B91C1C', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>{eroare}</div>}

        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" onClick={refuza} disabled={seTrimite} style={butonGol}>{t('priza', 'refuz')}</button>
          <button type="button" onClick={permite} disabled={!poatePermite} style={butonPlin(poatePermite)}>{seTrimite ? t('priza', 'seTrimite') : t('priza', 'permit')}</button>
        </div>
      </div>
    </div>
  );
}
